import React, { useContext, useState } from 'react';
import { RedXIcon } from 'assests/GmsIcons';
import { useNavigate } from "react-router";
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import { ChevronLeft } from 'Components/DesignSystem/Iconography/Icons';
import { IN_PROGRESS_STATUS, STOP } from '../../utils/Constants';
import CustomChip from '../../components/CustomChip';
import ABPromoteModal from '../../components/ABPromoteModal';
import { computeABTestResult } from '../../services/ABServices';
import { ABContext } from '../../contexts/ABContext';
import { getDaysBetween } from '../../utils/ABHelper';
import AuthManager from 'Common/AuthManager';
import ABStopModal from '../../components/ABStopModal';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { TABResult } from '../../types/ab';

/**
 * The results header (`okf/design-system/e2e.md` § 155). Same treatment as §§ 139/148:
 * the title was a `<label>` with no `htmlFor` at a hardcoded 24px, the row was tinted
 * `#FAFAFC`, and the back control was a bare `<button>` wrapping an SVG.
 *
 * **The Stop flow is untouched.** Ownership is still checked on click (against
 * `AuthManager.getAuthUser()` read on every render, not cached on mount), the control
 * model is still preselected as `selectedModelId` before the modal opens, Stop still
 * only renders while `status === IN_PROGRESS`, and Cancel still re-fetches
 * `computeABTestResult`. `finishABTest` remains the single POST, fired from the promote
 * modal.
 *
 * **Stop stays CLICKABLE for a non-owner, deliberately.** The legacy button painted
 * itself `tw-bg-gray-200 tw-cursor-not-allowed` while remaining live: clicking it fired
 * `handleStop`, which toasted "You can only stop A/B Tests that you have created." So it
 * looked disabled, wasn't, and explained itself only after you pressed it.
 *
 * The first cut of this migration made it really `disabled` — better UX, and wrong.
 * That toast is documented behaviour with two tests pinning it (`okf/abtesting/e2e.md`
 * § 3), and a disabled button never fires it. What is fixed instead is the *lie*: the
 * button no longer wears a disabled costume, and the reason is now available BEFORE the
 * click, as a tooltip, as well as after it, as the toast.
 */
type TABResultHeaderProps = {
    testName?: string;
    testId: string;
}

const ABResultHeader = ({ testName, testId }: TABResultHeaderProps) => {
    const navigate = useNavigate();
    const [showPromotModal, setShowPromoteModal] = useState<boolean>(false)
    const [showStopModal, setShowStopModal] = useState<boolean>(false)
    const abContext = useContext(ABContext);
    const { testData, setTestData, setSelectedModelId } = abContext;

    const remainingDays = getDaysBetween(testData?.test_duration, testData?.analyzed_duration);
    const dayText = remainingDays === 1 ? 'day' : 'days';

    const userobj = AuthManager.getAuthUser();
    const userProfile = userobj?.profile;
    const userid = userProfile?.id;
    const onBackPress = () => { navigate('/gms/ab'); setTestData(null) }

    const handlePostStop = async () => {
        setShowStopModal(false)
        setShowPromoteModal(true)
    }

    const handleStop = () => {
        if(testData?.ownership?.userid !== userid){
            toast.error('You can only stop A/B Tests that you have created.')
            return
        }
        const controlModel = testData?.result?.find((x: TABResult) => x?.is_control)?.id
        setSelectedModelId(controlModel)
        setShowStopModal(true)
    }

    const handleOnCancel = async () => {
        setShowPromoteModal(false);
        const testres = await computeABTestResult(testId as string)
        if (testres?.done) {
            setTestData(testres?.data)
        }
        else toast.info(testres?.message)
    }

    const isOwner = testData?.ownership?.userid === userid;

    /* Enabled either way — see the docblock. `handleStop` is the guard. */
    const stopButton = (
        <Button
            variant="danger"
            data-testid="result-abresultheader-button-1"
            onClick={handleStop}
            showIcon
            icon={<RedXIcon />}
        >
            {STOP}
        </Button>
    );

    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-edge tw-py-1.5">
            <div className="tw-flex tw-items-center tw-gap-3">
                <Button
                    variant="subtle"
                    size="sm"
                    onClick={onBackPress}
                    data-testid="ab-result-back-btn"
                    showIcon
                    icon={<ChevronLeft />}
                >
                    <span className="tw-sr-only">Back</span>
                </Button>
                <Typography variant="display" as="h1" className="tw-text-ink" data-testid="ab-result-title">
                    {testName ?? ''}
                </Typography>
                <CustomChip status={testData?.status} data-testid="ab-result-status" />
            </div>
            <div className="tw-flex tw-items-center tw-gap-3">
                {testData?.status === IN_PROGRESS_STATUS
                    ? <>
                        <CustomChip
                            data-testid="ab-result-days-left"
                            status={
                                <span>
                                    <span className='tw-font-semibold'>{remainingDays > 0 ? remainingDays : 0} {dayText} left </span>in the test
                                </span>
                            }
                        />
                        {/*
                          A disabled button is not focusable, so the tooltip is the only
                          way a non-owner learns why — and it beats the legacy behaviour,
                          where the explanation arrived as a toast AFTER clicking a
                          control that looked dead.
                        */}
                        {isOwner ? stopButton : (
                            <Tooltip
                                content="You can only stop A/B Tests that you have created."
                                placement="bottom"
                                usePortal
                                data-testid="ab-result-stop-blocked-tip"
                            >
                                <span>{stopButton}</span>
                            </Tooltip>
                        )}
                    </> : null}
                {/* <div className="tw-flex tw-items-center">
                    <div className="tw-px-2 tw-w-8 tw-rotate-90 tw-border tw-border-[#969fae]" />
                    <button className="button-style tw-w-max" data-testid="ab-result-promote-btn" onClick={onPromote}>
                        {PROMOTE}
                    </button>
                </div> */}
            </div>
            {showPromotModal ? <ABPromoteModal onCancel={handleOnCancel} /> : null}
            {showStopModal ? <ABStopModal isOpen={showStopModal} onCancel={() => setShowStopModal(false)} handleStop={handlePostStop} /> : null}
        </div>
    );
}

export default ABResultHeader;